import { normalizeRanges, range, type TimeRange } from '../time/ranges';
import type { Telemetry } from './telemetry';

/** A place the person wants kept out of their videos (home, work…). Stored on their device only. */
export interface PrivatePlace {
  readonly id: string;
  readonly latitude: number;
  readonly longitude: number;
}

export type ZoneAction = 'blur' | 'cut';
export const ZONE_ACTIONS: readonly ZoneAction[] = ['blur', 'cut'];

/** Radius choices in metres. */
export const ZONE_RADII = [150, 300, 600] as const;
export type ZoneRadius = (typeof ZONE_RADII)[number];

export interface ZoneSettings {
  /** Hide the time spent near private places (saved ones, and the ride's ends when `hideEnds`). */
  readonly enabled: boolean;
  readonly action: ZoneAction;
  /** Treat where the ride starts and ends as private too (rides often start at home). */
  readonly hideEnds: boolean;
  readonly radius: ZoneRadius;
}

export const DEFAULT_ZONE_SETTINGS: ZoneSettings = { enabled: true, action: 'blur', hideEnds: false, radius: 300 };

/** Extra time hidden on both sides of a zone visit, so the approach isn't shown either. */
const MARGIN_SEC = 1;
/** Leaving and re-entering within this time (GPS jitter at the edge) counts as one visit. */
const BRIDGE_SEC = 3;
const EARTH_RADIUS = 6_371_000;

/** Great-circle distance in metres. */
export function distanceMetres(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }): number {
  const toRad = Math.PI / 180;
  const dLat = (b.latitude - a.latitude) * toRad;
  const dLon = (b.longitude - a.longitude) * toRad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.latitude * toRad) * Math.cos(b.latitude * toRad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS * Math.asin(Math.min(1, Math.sqrt(h)));
}

const hasFix = (telemetry: Telemetry, i: number) => Number.isFinite(telemetry.latitude[i]!) && Number.isFinite(telemetry.longitude[i]!);

/** Position at a video time (nearest grid sample), or null without a GPS signal then. */
export function positionAt(telemetry: Telemetry, time: number): { latitude: number; longitude: number } | null {
  const i = Math.round((time - telemetry.start) * telemetry.rate);
  if (i < 0 || i >= telemetry.latitude.length || !hasFix(telemetry, i)) return null;
  return { latitude: telemetry.latitude[i]!, longitude: telemetry.longitude[i]! };
}

/** First and last positions with a signal: where the ride starts and ends. */
export function rideEnds(telemetry: Telemetry): PrivatePlace[] {
  const indices = [...telemetry.latitude.keys()].filter((i) => hasFix(telemetry, i));
  if (indices.length === 0) return [];
  return [indices[0]!, indices.at(-1)!].map((i, k) => ({ id: k === 0 ? 'ride-start' : 'ride-end', latitude: telemetry.latitude[i]!, longitude: telemetry.longitude[i]! }));
}

/** The private places this ride gets close to (within the radius at any moment). */
export function placesOnRide(telemetry: Telemetry, places: readonly PrivatePlace[], radius: number): PrivatePlace[] {
  return places.filter((place) => {
    for (let i = 0; i < telemetry.latitude.length; i++) {
      if (hasFix(telemetry, i) && distanceMetres(place, { latitude: telemetry.latitude[i]!, longitude: telemetry.longitude[i]! }) < radius) return true;
    }
    return false;
  });
}

/**
 * Source-time ranges spent near private places. Privacy wins every doubt: a
 * stretch without GPS signal next to a visit counts as part of it, and so do
 * the moments before the first and after the last GPS reading when the ride
 * starts or ends inside a zone (the camera often finds its fix only after
 * leaving the driveway).
 */
export function privateRanges(telemetry: Telemetry | null, places: readonly PrivatePlace[], settings: ZoneSettings, durationSec: number): TimeRange[] {
  if (!telemetry || !settings.enabled || !(durationSec > 0)) return [];
  const centres = [...places, ...(settings.hideEnds ? rideEnds(telemetry) : [])];
  const length = telemetry.latitude.length;
  if (centres.length === 0 || length === 0) return [];

  // 1 inside, 0 outside, -1 unknown (no signal).
  const state = new Int8Array(length).fill(-1);
  for (let i = 0; i < length; i++) {
    if (!hasFix(telemetry, i)) continue;
    const here = { latitude: telemetry.latitude[i]!, longitude: telemetry.longitude[i]! };
    state[i] = centres.some((centre) => distanceMetres(centre, here) < settings.radius) ? 1 : 0;
  }
  // Unknown stretches become inside when a known neighbour on either side is inside.
  let previous = -1;
  for (let i = 0; i < length; i++) {
    if (state[i] === -1) continue;
    if (state[i] === 1 || (previous !== -1 && state[previous] === 1)) state.fill(1, previous + 1, i);
    previous = i;
  }
  if (previous === -1) return [];
  if (state[previous] === 1) state.fill(1, previous + 1);

  const step = 1 / telemetry.rate;
  const visits: TimeRange[] = [];
  let open: number | null = null;
  for (let i = 0; i <= length; i++) {
    const inside = i < length && state[i] === 1;
    if (inside && open === null) open = i;
    if (!inside && open !== null) {
      const start = open === 0 ? 0 : telemetry.start + (open - 0.5) * step;
      const end = i === length ? durationSec : telemetry.start + (i - 0.5) * step;
      visits.push(range(start - MARGIN_SEC, end + MARGIN_SEC));
      open = null;
    }
  }
  const bridged = normalizeRanges(visits.map((visit) => range(visit.start, visit.end + BRIDGE_SEC))).map((visit) => range(visit.start, visit.end - BRIDGE_SEC));
  return bridged.map((visit) => range(Math.max(0, visit.start), Math.min(durationSec, visit.end))).filter((visit) => visit.end > visit.start);
}

/** Whether `time` falls inside any of the (sorted, merged) ranges. */
export function inRanges(ranges: readonly TimeRange[], time: number): boolean {
  let low = 0;
  let high = ranges.length - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    const r = ranges[mid]!;
    if (time < r.start) high = mid - 1;
    else if (time >= r.end) low = mid + 1;
    else return true;
  }
  return false;
}

/** Defensive parse of stored places; anything malformed is dropped. */
export function parsePlaces(raw: string | null): PrivatePlace[] {
  if (!raw) return [];
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return [];
    return value.filter(
      (item): item is PrivatePlace =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as PrivatePlace).id === 'string' &&
        Math.abs((item as PrivatePlace).latitude) <= 90 &&
        Math.abs((item as PrivatePlace).longitude) <= 180,
    );
  } catch {
    return [];
  }
}
